<template>
    <div id="dashboard-change-plan-view">
        <span class="dashboard-title mb-5">Select a New Plan</span>

        <div id="plans-container">
            <div class="plan-container --pro" :class="{ '--disabled': !canSelectLitePlan }">
                <span class="plan-title mb-1">Lite</span>
                <span class="plan-description">For enthusiasts working solo on one project at a time.</span>
                <hr class="my-5">
                <div class="plan-price-container mb-5">
                    <span class="price">${{ Number(((getProductPrice(PLAN_LITE.slug) - getProductDiscount(PLAN_LITE.slug)) / 12).toFixed(2)) }}</span>
                    <span class="price-label">/month billed annually</span>
                </div>
                <v-btn
                        :disabled="!canSelectLitePlan"
                        @click="onPlanSelection(PLAN_LITE.id)"
                        class="rounded-xl"
                        large
                        outlined
                >
                    Subscribe
                </v-btn>
                <hr class="mt-8 mb-5">
                <span class="included-collaborators mb-3">Single user</span>
                <div class="plan-info-container">
                    <span class="material-icons">check</span>
                    <span class="plan-info">Unlimited <b>Local</b> storage</span>
                </div>
                <div class="plan-info-container">
                    <span class="material-icons">check</span>
                    <span class="plan-info">Unlimited LUT creation</span>
                </div>
                <div class="plan-info-container">
                    <span class="material-icons">check</span>
                    <span class="plan-info">AI Auto Correct</span>
                </div>
                <div class="plan-info-container">
                    <span class="material-icons">check</span>
                    <span class="plan-info">AI Colour Extract</span>
                </div>
                <div class="plan-info-container">
                    <span class="material-icons">check</span>
                    <span class="plan-info">AI Colour Match</span>
                </div>
            </div>

            <div class="plan-container --pro" :class="{ '--disabled': !canSelectProPlan }">
                <span class="plan-title mb-1">Pro</span>
                <span class="plan-description">For professionals working solo or in very small teams.</span>
                <hr class="my-5">
                <div class="plan-price-container mb-5">
                    <span class="price">${{ Number(((getProductPrice(PLAN_PRO_YEARLY.slug + '-no-trial') - getProductDiscount(PLAN_PRO_YEARLY.slug + '-no-trial')) / 12).toFixed(2)) }}</span>
                    <span class="price-label">/month billed annually</span>
                </div>
                <v-btn
                        :disabled="!canSelectProPlan"
                        @click="onPlanSelection(!showYearlyPlans ? 2 : 3)"
                        class="rounded-xl"
                        large
                        outlined
                >
                    Subscribe
                </v-btn>
                <hr class="mt-8 mb-5">
                <span class="included-collaborators mb-3">Starts at 1 user</span>
                <div class="plan-info-container">
                    <span class="material-icons">arrow_back</span>
                    <span class="plan-info">Everything in Lite plus:</span>
                </div>
                <div class="plan-info-container">
                    <span class="material-icons">check</span>
                    <span class="plan-info">10 GB cloud storage</span>
                </div>
                <div class="plan-info-container">
                    <span class="material-icons">check</span>
                    <span class="plan-info d-flex align-center">NeuralToneAI <span class="material-icons ml-1" style="color: var(--primary)">diamond</span></span>
                </div>
                <div class="plan-info-container">
                    <span class="material-icons">check</span>
                    <span class="plan-info">Comment and collaborate</span>
                </div>
                <div class="plan-info-container">
                    <span class="material-icons">check</span>
                    <span class="plan-info">Share work with clients</span>
                </div>
            </div>

            <div class="plan-container --team" :class="{ '--disabled': !canSelectTeamPlan }">
                <span class="plan-title mb-1">Team</span>
                <span class="plan-description">For teams in need of absolute control and collaboration.</span>
                <hr class="my-5">
                <div class="plan-price-container mb-5">
                    <span class="price">${{ Number(((getProductPrice(PLAN_TEAM_YEARLY.slug) - getProductDiscount(PLAN_TEAM_YEARLY.slug)) / 12).toFixed(2)) }}</span>
                    <span class="price-label">/month billed annually</span>
                </div>
                <v-btn
                        :disabled="!canSelectTeamPlan"
                        @click="onPlanSelection(!showYearlyPlans ? 4 : 5)"
                        class="rounded-xl"
                        large
                        outlined
                >
                    Subscribe
                </v-btn>
                <hr class="mt-8 mb-5">
                <span class="included-collaborators mb-3">Starts at 3 users</span>
                <div class="plan-info-container">
                    <span class="material-icons">arrow_back</span>
                    <span class="plan-info">Everything in Pro plus:</span>
                </div>
                <div class="plan-info-container">
                    <span class="material-icons">check</span>
                    <span class="plan-info">25 GB cloud storage</span>
                </div>
                <div class="plan-info-container">
                    <span class="material-icons">check</span>
                    <span class="plan-info">Team-only comments</span>
                </div>
                <div class="plan-info-container">
                    <span class="material-icons">check</span>
                    <span class="plan-info">Additional user roles</span>
                </div>
                <div class="plan-info-container">
                    <span class="material-icons">check</span>
                    <span class="plan-info">Client-assignable statuses</span>
                </div>
                <div class="plan-info-container">
                    <span class="material-icons">check</span>
                    <span class="plan-info">Priority support</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {mapGetters, mapState} from "vuex";

export default {
    data() {
        return {
            showYearlyPlans: true,
        };
    },

    async created() {
        await this.$store.dispatch("User/fetchCurrentPlan");
        this.$store.dispatch("Products/fetchAllPrices");
    },

    computed: {
        ...mapState("User", ["account", "currentPlan"]),
        ...mapState("Plans", ['PLAN_LITE', 'PLAN_LITE_FREE', 'PLAN_PRO_YEARLY', 'PLAN_TEAM_YEARLY', 'selectedPlan']),
        ...mapGetters("Plans", ["planData"]),
        ...mapGetters("Products", ['getProductPrice', 'getProductDiscount']),

        canSelectLitePlan() {
          return this.currentPlan.id == this.PLAN_LITE_FREE.id
        },

        canSelectProPlan() {
            return true;
        },

        canSelectTeamPlan() {
            return true;
        },
    },

    methods: {
        onPlanSelection(plan_id) {
            if (plan_id == this.PLAN_LITE.id && !this.canSelectLitePlan) return;
            this.$store.dispatch("Plans/setSelectedPlan", plan_id);
        },

    },
};
</script>

<style lang="scss" scoped>
#plans-container {
    display: grid;
	grid-template-columns: 1fr 1fr 1fr;
	grid-column-gap: 25px;
    align-content: stretch;
}

.plan-container {
    display: flex;
    flex-direction: column;
    // border: 1px solid #ccc;
	border-radius: 20px;
	background: rgb(13, 3, 20);
	padding: 25px 18px;

    &.--disabled {
        opacity: 0.5;
    }

    span {
        line-height: 1.5;
        color: white;
    }

    .plan-title {
        // font-weight: bold;
		font-size: 1.5rem;
        color: white;
        font-weight: bold;
    }

    .plan-price-container {
        .price {
			font-size: 1.8rem;
            font-weight: bold;
        }

        .price-label {
			font-size: 0.9rem;
			margin-left: 3px;
            color: #DBCFFC;
        }
    }

    .plan-info-container {
        display: flex;
        align-items: center;
		&:not(:last-of-type) {
			margin-bottom: 1px;
		}

        .material-icons {
            font-size: 16px;
            margin-right: 5px;
            font-weight: bold;
        }
    }

    .v-btn {
        background: black;
        color: white;
        border: none;

        a {
            color: white;
        }
    }

    &.--pro {
        background: white;

        .plan-title,
        .material-icons {
            color: rgb(5, 5, 5);
        }

        span {
            color: rgb(5, 5, 5);
        }

        .price-label {
            color: var(--primary);
        }
    }

    &.--team {
        background: #8D68F6;

        .plan-title,
        .material-icons {
            color: white;
        }
    }

    &.--enterprise {
        background: #2B088E;

        .plan-title,
        .material-icons {
            color: white;
        }
    }
}

.colord {
    color: #989898 !important;
}

.view-title {
    color: white;
    font-size: 2rem;
    text-transform: uppercase;
    text-align: center;
    margin: 50px 0;
}

hr {
	background-color: #ccc;
	opacity: 0.5;
	height: 1px;
	border: 0;
}
</style>
